import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LabApiError, labApi, loginUrl, startLabRun } from './api'
import { formatCost, modelLabel, runPath, scenePath } from './format'
import { SCENES, sceneApi, type SceneSlug } from './scenes'
import { useT } from './i18n'
import { useSignedIn } from './session'
import type { LabCatalogModel, LabRunLimits } from './types'

// Mirrors apiserver labSubjectRe / labAuthorRe.
const ENGLISH = /^[A-Za-z0-9][A-Za-z0-9 .,:;!?'"()&+/@#_-]{2,119}$/
const NAME = /^[A-Za-z0-9][A-Za-z0-9 ._-]{1,31}$/

export function RunPanel({ suggested, limits, defaultSubject, scene = 'pelican-test' }: { suggested: string[]; limits: LabRunLimits; defaultSubject: string; scene?: SceneSlug }) {
  const signedIn = useSignedIn()
  const navigate = useNavigate()
  const t = useT()
  const rp = t.runPanel
  const sc = t.scene[scene]
  const info = SCENES[scene]
  const KIND_OPTIONS = info.kinds
  const [catalog, setCatalog] = useState<LabCatalogModel[] | null>(null)
  const [filter, setFilter] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [kinds, setKinds] = useState<string[]>(KIND_OPTIONS.map(([k]) => k))
  const [subject, setSubject] = useState(defaultSubject)
  const [name, setName] = useState('')
  const [error, setError] = useState<{ message: string; topUp?: boolean; signIn?: boolean } | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!signedIn || limits.user_runs === false) return
    let cancelled = false
    labApi<{ items: LabCatalogModel[] }>(`${sceneApi(scene)}/models`).then(({ items }) => {
      if (cancelled) return
      setCatalog(items)
      setPicked((current) => current.length ? current : suggested.filter((m) => items.some((i) => i.name === m)).slice(0, Math.min(4, limits.max_models)))
    }).catch(() => { if (!cancelled) setCatalog([]) })
    return () => { cancelled = true }
  }, [signedIn, suggested, limits.max_models, limits.user_runs, scene])

  const estimate = useMemo(() => picked.reduce((sum, m) => sum + (catalog?.find((c) => c.name === m)?.max_cost_per_drawing ?? 0) * kinds.length, 0), [picked, catalog, kinds])
  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase()
    return (catalog ?? []).filter((c) => !q || c.name.toLowerCase().includes(q) || c.display_name.toLowerCase().includes(q)).slice(0, 80)
  }, [catalog, filter])

  // The Worker refuses user runs of this scene for now (403 user_runs_disabled).
  if (limits.user_runs === false) {
    return (
      <div className="panel signin-panel">
        <div>
          <p className="eyebrow accent">{rp.comingSoon}</p>
          <p className="card-title">{rp.comingSoonTitle(scene)}</p>
          <p className="muted">{rp.comingSoonBody}</p>
        </div>
      </div>
    )
  }

  if (!signedIn) {
    return (
      <div className="panel signin-panel">
        <div>
          <p className="card-title">{rp.signInTitle(info.name)}</p>
          <p className="muted">{rp.signInBody(limits.max_models, info.remixable, sc.unit, sc.units)}</p>
        </div>
        <a href={loginUrl(`${scenePath(scene)}#run`)} className="btn btn-ink">{rp.signInCta}</a>
      </div>
    )
  }

  // Fixed-prompt scenes ignore the subject; the Worker sends its own.
  const subjectOk = !info.remixable || ENGLISH.test(subject.trim())
  const nameOk = !name.trim() || (NAME.test(name.trim()) && !/sand\s*base/i.test(name))
  const overBudget = estimate > limits.max_cost_usd
  const canRun = picked.length > 0 && kinds.length > 0 && subjectOk && nameOk && !overBudget && !busy
  const toggle = (m: string) => setPicked((cur) => cur.includes(m) ? cur.filter((x) => x !== m) : cur.length >= limits.max_models ? cur : [...cur, m])

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const runId = await startLabRun(scene, { models: picked, kinds, subject: subject.trim(), displayName: name.trim() || undefined })
      navigate(runPath(runId, scene))
    } catch (e) {
      const status = e instanceof LabApiError ? e.status : 0
      setError({ message: e instanceof Error ? e.message : rp.couldNotStart, topUp: status === 402, signIn: status === 401 })
      setBusy(false)
    }
  }

  return (
    <div className="panel run-panel">
      <div className="stack">
        <div className="row-between">
          <p className="eyebrow muted">{rp.modelsLabel(picked.length, limits.max_models)}</p>
          {picked.length ? <button type="button" onClick={() => setPicked([])} className="link-btn">{rp.clear}</button> : null}
        </div>
        <input className="field" placeholder={rp.searchModels} value={filter} onChange={(e) => setFilter(e.target.value)} aria-label={rp.searchModels} />
        <div className="chips">{picked.map((m) => <button key={m} type="button" onClick={() => toggle(m)} className="chip" aria-label={rp.remove(modelLabel(m))}>{modelLabel(m)} ×</button>)}</div>
        <div className="model-list" role="group" aria-label={rp.availableModels}>
          {visible.map((c) => (
            <label key={c.name} className="model-option">
              <span className="row">
                <input type="checkbox" checked={picked.includes(c.name)} onChange={() => toggle(c.name)} disabled={!picked.includes(c.name) && picked.length >= limits.max_models} />
                <span>{c.display_name || modelLabel(c.name)}{c.sota ? <span className="sota-badge">SOTA</span> : null}<span className="mono small subtle"> {c.name}</span><span className="model-release">{t.common.publishedAt(c.published_at)}</span></span>
              </span>
              <span className="mono small subtle">≤{formatCost(c.max_cost_per_drawing)}</span>
            </label>
          ))}
          {catalog === null ? <p className="pad muted">{rp.loadingModels}</p> : null}
          {catalog?.length === 0 ? <p className="pad muted">{rp.modelsUnavailable}</p> : null}
        </div>
      </div>
      <div className="stack">
        <fieldset className="kinds">
          <legend className="eyebrow muted">{rp.tests}</legend>
          {KIND_OPTIONS.map(([k, label]) => (
            <label key={k} className="row"><input type="checkbox" checked={kinds.includes(k)} onChange={() => setKinds((cur) => cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k])} />{sc.kinds[k] ?? label}</label>
          ))}
        </fieldset>
        {info.remixable ? (
          <label className="stack-sm">
            <span className="eyebrow muted">{rp.subjectLabel}</span>
            <input className="field" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={120} aria-invalid={!subjectOk} aria-describedby="subject-help" />
            <span id="subject-help" className={`small ${subjectOk ? 'muted' : 'error'}`}>{subjectOk ? (subject.trim() === defaultSubject ? rp.subjectKeep(sc.unit) : rp.subjectRemix) : rp.subjectInvalid}</span>
          </label>
        ) : null}
        <label className="stack-sm">
          <span className="eyebrow muted">{rp.nameLabel}</span>
          <input className="field" value={name} onChange={(e) => setName(e.target.value)} maxLength={32} placeholder={rp.namePlaceholder} aria-invalid={!nameOk} aria-describedby={nameOk ? undefined : 'name-help'} />
          {!nameOk ? <span id="name-help" className="small error">{rp.nameInvalid}</span> : null}
        </label>
        <div className="run-foot">
          <p className="row-between"><span className="muted">{rp.worstCase(picked.length * kinds.length, sc.units)}</span><b className={`num ${overBudget ? 'error' : ''}`}>≤ {formatCost(estimate)}</b></p>
          <p className="small muted">{rp.billing(`$${limits.max_cost_usd.toFixed(2)}`, limits.daily_runs)}</p>
          {error ? (
            <p role="alert" className="error">
              {error.message}{' '}
              {error.topUp ? <a href="/console/billing">{rp.topUp}</a> : null}
              {error.signIn ? <a href={loginUrl(`${scenePath(scene)}#run`)}>{rp.signInAgain}</a> : null}
            </p>
          ) : null}
          <button type="button" disabled={!canRun} onClick={submit} className="btn btn-ink btn-block">{busy ? rp.starting : rp.runScene(scene)}</button>
        </div>
      </div>
    </div>
  )
}
